import {
  Chip,
  Colors,
  Flex,
  IconChevronRight,
  Typography,
} from '@banhala/ui-kit';
import { useAtomValue, useSetAtom } from 'jotai';
import type { BeautyInfo } from 'shared/src/api/getMemberInfo/types';
import type { BeautyOption } from 'shared/src/api/myPageService/getBeautyInfo/types';
import { Tooltip } from 'shared/src/component/Tooltip';
import { MAX_SKIN_PROBLEMS } from '../constants';
import { useMyPersonalizationForm } from '../state';
import IconHelpTooltip from './IconHelpTooltip';
import ImgPersonalColor from './ImgPersonalColor';

type MyBeautySectionProps = {
  tooltipDismissKey: number;
  skinToneOptions: BeautyOption[];
  skinTypeOptions: BeautyOption[];
  skinProblemOptions: BeautyOption[];
  onPressFindPersonalColor: () => void;
};

const TOOLTIP_TEXT =
  '입력해주신 정보는 다른 분들의 상품 리뷰 참고용으로만 제공되며 개인식별정보는 절대 공개되지 않습니다';

const MyBeautySection = ({
  tooltipDismissKey,
  skinToneOptions,
  skinTypeOptions,
  skinProblemOptions,
  onPressFindPersonalColor,
}: MyBeautySectionProps) => {
  const { formAtom, toggleSkinProblemAtom } = useMyPersonalizationForm();
  const { skinTone, skinType, skinProblems } = useAtomValue(formAtom);
  const setForm = useSetAtom(formAtom);
  const toggleSkinProblem = useSetAtom(toggleSkinProblemAtom);

  const setSkinTone = (value: BeautyInfo | null) =>
    setForm(prev => ({ ...prev, skinTone: value }));
  const setSkinType = (value: BeautyInfo | null) =>
    setForm(prev => ({ ...prev, skinType: value }));

  return (
    <Flex>
      <Flex.Row
        alignItems="center"
        padding={{ top: 24, bottom: 16 }}
        gap={4}
      >
        <Typography type="h5" color="content_primary">
          피부 정보
        </Typography>
        <Tooltip
          key={tooltipDismissKey}
          label={TOOLTIP_TEXT}
          contentStyle={{ maxWidth: 300 }}
        >
          <IconHelpTooltip size={24} />
        </Tooltip>
      </Flex.Row>

      {/* 퍼스널 컬러 */}
      <ChipGroup title="퍼스널 컬러를 선택해주세요">
        <Chip
          size="medium"
          state={skinTone == null ? 'selected' : 'enabled'}
          onPress={() => setSkinTone(null)}
        >
          선택 안 함
        </Chip>
        {skinToneOptions.map(option => (
          <Chip
            key={option.sno}
            size="medium"
            state={skinTone?.sno === option.sno ? 'selected' : 'enabled'}
            onPress={() => setSkinTone(option)}
          >
            {option.name}
          </Chip>
        ))}
      </ChipGroup>

      {skinTone == null && (
        <FindPersonalColorBanner onPress={onPressFindPersonalColor} />
      )}

      {/* 피부 타입 */}
      <Flex padding={{ top: 32 }}>
        <ChipGroup title="피부 타입을 선택해주세요">
          <Chip
            size="medium"
            state={skinType == null ? 'selected' : 'enabled'}
            onPress={() => setSkinType(null)}
          >
            선택 안 함
          </Chip>
          {skinTypeOptions.map(option => (
            <Chip
              key={option.sno}
              size="medium"
              state={skinType?.sno === option.sno ? 'selected' : 'enabled'}
              onPress={() => setSkinType(option)}
            >
              {option.name}
            </Chip>
          ))}
        </ChipGroup>
      </Flex>

      {/* 피부 고민 */}
      <Flex padding={{ top: 32 }}>
        <ChipGroup
          title={`피부 고민을 선택해주세요 (최대 ${MAX_SKIN_PROBLEMS}개)`}
        >
          <Chip
            size="medium"
            state={skinProblems.length === 0 ? 'selected' : 'enabled'}
            onPress={() => toggleSkinProblem(null)}
          >
            선택 안 함
          </Chip>
          {skinProblemOptions.map(option => {
            const isSelected = skinProblems.some(p => p.sno === option.sno);
            return (
              <Chip
                key={option.sno}
                size="medium"
                state={isSelected ? 'selected' : 'enabled'}
                onPress={() => toggleSkinProblem(option)}
              >
                {option.name}
              </Chip>
            );
          })}
        </ChipGroup>
      </Flex>
    </Flex>
  );
};

const ChipGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Flex>
      <Typography type="body2" color="content_secondary">
        {title}
      </Typography>
      <Flex.Row flexWrap="wrap" gap={8} rowGap={12} padding={{ top: 12 }}>
        {children}
      </Flex.Row>
    </Flex>
  );
};

const FindPersonalColorBanner = ({ onPress }: { onPress: () => void }) => {
  return (
    <Flex
      onPress={onPress}
      borderRadius={10}
      backgroundColor="bg_secondary"
      padding={{ horizontal: 20, vertical: 18 }}
      margin={{ top: 16 }}
    >
      <Flex.Row alignItems="center">
        <Flex padding={{ right: 10 }}>
          <ImgPersonalColor />
        </Flex>
        <Flex flex={1} padding={{ right: 12 }}>
          <Typography type="subtitle1" color="content_primary">
            내 퍼스널컬러 찾기
          </Typography>
          <Typography type="body2" color="content_secondary">
            지금 확인하고 맞춤 상품도 봐요
          </Typography>
        </Flex>
        <IconChevronRight
          size={20}
          fill={Colors.content_secondary}
        />
      </Flex.Row>
    </Flex>
  );
};

export default MyBeautySection;
